import { differenceWith, intersectionWith } from "lodash";

import { OrderedEntryGroupApi, OrderedListingGroupApi, OrderedSectionApi } from "lib/api";
import { ComponentTemplate, MultipleItemRequirement, OrderedEntryGroup, OrderedListingGroup, OrderedSection, Requirement, RequirementItem, Section, SingleItemRequirement } from "lib/data/model";
import { MultipleItemRequirementType } from "lib/data/model/enumerations";

export class ComponentTemplateService {

	public static getTemplatesFromRequirements = (requirements: Array<Requirement>)
		: ComponentTemplate[] => {

		// Initialize Template Array
		const templates: ComponentTemplate[] = [];

		// Loop Through Requirements
		requirements.forEach((requirement) => {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;

					// Add Template To Array
					templates.push(singleItemRequirement.item.item);

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;

					// Loop Through Requirement Items
					multipleItemRequirement.items.forEach((item) => {

						// Add Template To Array
						templates.push(item.item);
					});

					// Exit Switch
					break;
			}
		});

		// Return Templates
		return templates;
	}

	public static getRequirementFromTemplate = (requirements: Array<Requirement>, template: ComponentTemplate)
		: Requirement | undefined => {

		// Loop Through Requirements
		for (const requirement of requirements) {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;

					// Check Requirement's Template
					if (singleItemRequirement.item.item.id === template.id) {

						// Return Requirement
						return singleItemRequirement;
					}

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;

					// Loop Through Requirement Items
					for (const item of multipleItemRequirement.items) {

						// Check Requirement Item's Template
						if (item.item.id === template.id) {

							// Return Requirement
							return multipleItemRequirement;
						}
					}

					// Exit Switch
					break;
			}
		}

		// Return Nothing
		return undefined;
	}

	public static getRequirementItemFromTemplate = (requirements: Array<Requirement>, template: ComponentTemplate)
		: RequirementItem<ComponentTemplate> | undefined => {

		// Loop Through Requirements
		for (const requirement of requirements) {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;

					// Check Requirement's Template
					if (singleItemRequirement.item.item.id === template.id) {

						// Return Requirement Item
						return singleItemRequirement.item;
					}

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.ComponentTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;

					// Loop Through Requirement Items
					for (const item of multipleItemRequirement.items) {

						// Check Requirement Item's Template
						if (item.item.id === template.id) {

							// Return Requirement Item
							return item;
						}
					}

					// Exit Switch
					break;
			}
		}

		// Return Nothing
		return undefined;
	}

	public static getAvailableTemplates = async (
		section: Section,
		requirements: Array<Requirement>,
		components: Array<OrderedEntryGroup | OrderedListingGroup | OrderedSection>
	): Promise<Array<RequirementItem<ComponentTemplate>>> => {

		// Initialize Promises
		const promises: Array<Promise<void>> = [];

		// Loop Through Existing Components
		for (const component of components) {

			switch (component.$type) {
				case OrderedEntryGroup.$type:

					// Promise To Get Template
					promises.push(new Promise<void>(async (resolve) => {

						const entryGroupTemplate = await OrderedEntryGroupApi.getTemplate(section, component as OrderedEntryGroup, false);

						if (entryGroupTemplate !== undefined) {

							(component as OrderedEntryGroup).item.template = entryGroupTemplate;
						}

						resolve();
					}));

					break;

				case OrderedListingGroup.$type:

					// Promise To Get Template
					promises.push(new Promise<void>(async (resolve) => {

						const listingGroupTemplate = await OrderedListingGroupApi.getTemplate(section, component as OrderedListingGroup, false);

						if (listingGroupTemplate !== undefined) {

							(component as OrderedListingGroup).item.template = listingGroupTemplate;
						}

						resolve();
					}));

					break;

				case OrderedSection.$type:

					// Promise To Get Template
					promises.push(new Promise<void>(async (resolve) => {

						const sectionTemplate = await OrderedSectionApi.getTemplate(section, component as OrderedSection, false);

						if (sectionTemplate !== undefined) {

							(component as OrderedSection).item.template = sectionTemplate;
						}

						resolve();
					}));

					break;
			}
		}

		// Await Promises
		await Promise.all(promises);

		// Initialize Available Templates
		const availableTemplates: Set<RequirementItem<ComponentTemplate>> = new Set<RequirementItem<ComponentTemplate>>();

		// Loop Through Requirements
		for (const requirement of requirements) {

			// Switch On Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.ComponentTemplate:

					// Cast Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;

					// Check If Repeatable
					if (singleItemRequirement.isRepeatable) {

						// Get Requirement Template(s)
						const singleItemRequirementTemplates = ComponentTemplateService.getTemplatesFromRequirements([singleItemRequirement]);

						// Add Template Data Field To Addable List
						singleItemRequirementTemplates.forEach((componentTemplate) => {

							// Get Requirement Item For Template
							const requirementItem = ComponentTemplateService.getRequirementItemFromTemplate([singleItemRequirement], componentTemplate);

							// Check Requirement Item
							if (requirementItem !== undefined) {

								// Add Requirement Item Or Data Field Template
								availableTemplates.add(requirementItem);
							}
						});
					}
					else {

						// Get Matching Data Field
						const matchingDataField = components.find((component) => {

							// Return Whether Component Found Based On Template
							if (component.item.template !== undefined) {

								return component.item.template !== undefined && component.item.template.id === singleItemRequirement.item.item.id;
							}
							return false;
						});

						// Check Values For Matching Data Field
						if (matchingDataField === undefined) {

							// Return Requirement Item
							availableTemplates.add(singleItemRequirement.item);
						}
					}

					break;

				case MultipleItemRequirement.$type.ComponentTemplate:

					// Cast Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;

					// Get Fields
					const multipleItemRequirementTemplates = multipleItemRequirement.items.map((i) => i.item);
					const existingComponentTemplates = components.map((component) => {

						// Return Component Template
						return component.item.template;
					}).filter((i) => i !== undefined) as ComponentTemplate[];

					// Get All Fulfilled Fields
					const fulfilledTemplates = intersectionWith(existingComponentTemplates, multipleItemRequirementTemplates, (a, b) => a.id === b.id);

					// Switch On Multiple Item Requirement Type
					switch (multipleItemRequirement.requirementType) {

						case MultipleItemRequirementType.AtLeast:

							const availableTemplates_atLeast = ComponentTemplateService.getAvailableMultipleItemRequirementTemplates(multipleItemRequirement, existingComponentTemplates, multipleItemRequirementTemplates);

							// Loop Through Available Templates
							availableTemplates_atLeast.forEach((item) => availableTemplates.add(item));

							break;

						case MultipleItemRequirementType.AtMost:
						case MultipleItemRequirementType.Exactly:

							// Check Fulfilled Fields
							if (fulfilledTemplates.length < multipleItemRequirement.fulfillmentCount) {

								const availableTemplates_atMost = ComponentTemplateService.getAvailableMultipleItemRequirementTemplates(multipleItemRequirement, existingComponentTemplates, multipleItemRequirementTemplates);

								// Loop Through Available Templates
								availableTemplates_atMost.forEach((item) => availableTemplates.add(item));
							}

							break;

						case MultipleItemRequirementType.All:

							// Check Fulfilled Fields
							if (fulfilledTemplates.length < multipleItemRequirement.items.length) {

								const availableTemplates_all = ComponentTemplateService.getAvailableMultipleItemRequirementTemplates(multipleItemRequirement, existingComponentTemplates, multipleItemRequirementTemplates);

								// Loop Through Available Templates
								availableTemplates_all.forEach((item) => availableTemplates.add(item));
							}

							break;
					}

					break;
			}
		}

		// Return Available Templates
		return Array.from(availableTemplates);
	}

	private static getAvailableMultipleItemRequirementTemplates = (
		multipleItemRequirement: MultipleItemRequirement<ComponentTemplate>,
		existingComponentTemplates: ComponentTemplate[],
		requirementTemplates: ComponentTemplate[]
	): Array<RequirementItem<ComponentTemplate>> => {

		// Initialize Array
		const availableTemplates: Array<RequirementItem<ComponentTemplate>> = [];

		const remainingTemplates = differenceWith(requirementTemplates, existingComponentTemplates, (a, b) => a.id === b.id);

		// Loop Through Remaining Templates
		remainingTemplates.forEach((componentTemplate) => {

			// Get Requirement Item For Template
			const requirementItem = ComponentTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], componentTemplate);

			// Check Requirement Item
			if (requirementItem !== undefined) {

				// Add Requirement Item
				availableTemplates.push(requirementItem);
			}
		});

		// Return Available Templates
		return availableTemplates;
	}
}
